import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/prerender";
import { blogPosts as staticPosts, formatDate } from "@/data/blogPosts";

export interface BlogPostData {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  thumb?: string;
  tag: string;
  imageCaption?: string;
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  source: "db" | "static";
}

const fetchDbPosts = async (): Promise<BlogPostData[]> => {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false });

  if (error || !data) return [];

  return data.map((p: any) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt || "",
    date: p.created_at.split("T")[0],
    image: p.image_url || "",
    tag: p.tag || "",
    imageCaption: p.image_caption || undefined,
    content: p.content,
    metaTitle: p.meta_title || undefined,
    metaDescription: p.meta_description || undefined,
    ogImage: p.og_image_url || undefined,
    source: "db" as const,
  }));
};

export const useBlogPosts = (enabled: boolean = true) => {
  const { data: dbPosts = [], isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: fetchDbPosts,
    staleTime: 60_000,
    enabled,
  });

  // Merge: DB posts first, then static posts that aren't duplicated by slug
  const dbSlugs = new Set(dbPosts.map((p) => p.slug));
  const staticMapped: BlogPostData[] = staticPosts
    .filter((p) => !dbSlugs.has(p.slug))
    .map((p) => ({ ...p, source: "static" as const }));

  const allPosts = [...dbPosts, ...staticMapped].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return { posts: allPosts, isLoading };
};

export const useBlogPost = (slug: string | undefined) => {
  const { posts, isLoading } = useBlogPosts();
  const post = slug ? posts.find((p) => p.slug === slug) : undefined;
  return { post, isLoading };
};

export { formatDate };
